import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "./api";
import { useAuth } from "./auth";

type ActiveModelSummary = {
  runId: string;
  runShort: string;
  configLabel: string;
  updatedAt: string;
};

type ActiveModelContextValue = {
  summary: ActiveModelSummary | null;
  error: string | null;
  refresh: () => void;
};

const ActiveModelContext = createContext<ActiveModelContextValue | null>(null);

export function ActiveModelProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const idToken = user?.idToken ?? null;
  const [summary, setSummary] = useState<ActiveModelSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reqSeq = useRef(0);
  const lastLoadedToken = useRef<string | null>(null);

  const load = useCallback(async (idToken: string) => {
    const mySeq = ++reqSeq.current;
    const active = await api.getActiveModel({ idToken });
    if (mySeq !== reqSeq.current) return;

    if (!active?.run_id) {
      setSummary(null);
      setError(null);
      return;
    }

    const [run, configs] = await Promise.all([
      api.getModelRun(active.run_id, { idToken }),
      api.listModelConfigs({ idToken, limit: 50 }),
    ]);
    if (mySeq !== reqSeq.current) return;

    const cfg = configs.find((c) => c.config_id === run.config_id);
    const configLabel = (cfg?.label ?? cfg?.display_name ?? run.config_id).trim() || run.config_id;
    setSummary({
      runId: active.run_id,
      runShort: active.run_id.slice(0, 8),
      configLabel,
      updatedAt: active.updated_at,
    });
    setError(null);
  }, []);

  const summaryForCtx = idToken ? summary : null;
  const errorForCtx = idToken ? error : null;

  useEffect(() => {
    if (!idToken) {
      reqSeq.current += 1;
      lastLoadedToken.current = null;
      return;
    }
    if (lastLoadedToken.current === idToken) return;
    lastLoadedToken.current = idToken;
    void load(idToken).catch((err) => {
      // Keep the last known summary on transient failures.
      const msg = String((err as Error | undefined)?.message ?? err);
      setError(msg);
      console.warn("Failed to load active model summary", err as Error);
    });
  }, [idToken, load]);

  const refresh = useCallback(() => {
    if (!idToken) return;
    lastLoadedToken.current = idToken;
    void load(idToken).catch((err) => {
      const msg = String((err as Error | undefined)?.message ?? err);
      setError(msg);
      console.warn("Failed to refresh active model summary", err as Error);
    });
  }, [idToken, load]);

  return (
    <ActiveModelContext.Provider value={{ summary: summaryForCtx, error: errorForCtx, refresh }}>
      {children}
    </ActiveModelContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActiveModel(): ActiveModelContextValue {
  const ctx = useContext(ActiveModelContext);
  if (!ctx) throw new Error("useActiveModel must be used inside <ActiveModelProvider>");
  return ctx;
}
